import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings, Search, Home as HomeIcon } from 'lucide-react';
import { ModuleIcon } from '../config/moduleIcons';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useNavPrefetch } from '../hooks/core';
import { useModule, MODULES, ModuleId } from '../contexts/ModuleContext';
import { visibleNavItems } from '../lib/utils/navVisibility';
import { useCanOpenPage } from '../modules/useCanOpenPage';
import type { UserRole } from '../types/firestore';
import { UserAvatar } from './Avatar';
import { isTypesenseEnabled } from '../config/typesense';
import { useCommandPalette } from '../contexts/CommandPaletteContext';
import { useNavigationRendered } from '../contexts/NavigationContext';
import { useMyVehicleEligibility } from '../hooks/driver/useMyVehicle';
import { LINK_REVIEW_PATH } from '../modules/billing/nav';
import { LinkReviewNavBadge } from './Collections/LinkReviewNavBadge';
import { canAccessIssueTriage } from '../lib/issueTriageAccess';
import { groupBadge } from '../lib/agents/agentTaskNavBadge';
import { buttonClasses } from './forms/Button';

/**
 * A red count pill (dropdown headings and dropdown rows: the pending agent tasks on "Agent Activity").
 * `bare`: no margin and no screen-reader text, for a parent that spaces it and names the count itself.
 */
function CountBadge({ count, bare = false }: { count: number; bare?: boolean }) {
  if (count <= 0) return null;
  return (
    <>
      <span aria-hidden="true" className={`${bare ? '' : 'ml-1.5 '}inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full`}>
        {count > 99 ? '99+' : count}
      </span>
      {!bare && <span className="sr-only">{` (${count} pending)`}</span>}
    </>
  );
}

interface NavigationProps {
  title?: string;
}

/**
 * Navigation wrapper that skips rendering when a parent ModuleLayout or
 * PageLayout has already rendered the nav bar. This prevents duplicate
 * top bars without violating React's rules of hooks.
 *
 * INVARIANT: This wrapper must contain ONLY the dedup hook + conditional
 * return. All state, effects, and other hooks belong in NavigationInner.
 * Adding hooks here before the early return will violate Rules of Hooks.
 */
export function Navigation(props: NavigationProps) {
  const alreadyRendered = useNavigationRendered();
  if (alreadyRendered) return null;
  return <NavigationInner {...props} />;
}

function NavigationInner({ title = 'OfficeHub' }: NavigationProps) {
  const { user, logout, isAdmin, isManagerOrAdmin, hasRole } = useAuth();
  // Only a strictly-assigned driver gets the link (the server decides; no direct reads here).
  const { data: myVehicle } = useMyVehicleEligibility();
  const { currentOrganization } = useOrganization();
  const { currentModule, setCurrentModule, navItems: allNavItems, hasModuleAccess, isOwner } = useModule();
  const { prefetch } = useNavPrefetch(currentOrganization?.organizationId);
  const location = useLocation();
  const canOpenPage = useCanOpenPage();

  // Filter nav items by role requirements (see lib/utils/navVisibility).
  const navItems = visibleNavItems(allNavItems, {
    isAdmin: isAdmin(),
    isManagerOrAdmin: isManagerOrAdmin(),
    hasRole: (role) => hasRole(role as UserRole),
    isExternal: user?.isExternal === true,
    canOpenPage,
    role: user?.role,
  });
  // Below `xl` every tab is folded into the closed Menu button, so it carries the total of the counts it hides.
  // A group heading's own `badge` is left out, as it is on the heading itself (groupBadge counts children only).
  // Link Review is left out too: LinkReviewNavBadge loads its count only while a menu that shows it is open, so
  // the top bar of every page never reads it (#3464); adding it here would make every page pay that read.
  const menuBadge = navItems.reduce((sum, item) => sum + (item.children ? groupBadge(item) : (item.badge ?? 0)), 0);
  const { open: openSearch } = useCommandPalette();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showModuleMenu, setShowModuleMenu] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const moduleMenuRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const navDropdownRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const setNavDropdownRef = useCallback((label: string, el: HTMLDivElement | null) => {
    if (el) {
      navDropdownRefs.current.set(label, el);
    } else {
      navDropdownRefs.current.delete(label);
    }
  }, []);

  // Check if user has access to error tracking
  const canAccessErrorTracking = user?.email === 'nick@onpointgen.com' || hasRole('Developer') || hasRole('Admin');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (moduleMenuRef.current && !moduleMenuRef.current.contains(event.target as Node)) {
        setShowModuleMenu(false);
      }
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target as Node)) {
        setShowMobileNav(false);
      }
      if (openDropdown) {
        const activeRef = navDropdownRefs.current.get(openDropdown);
        if (activeRef && !activeRef.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  if (!user) {
    return null;
  }

  const matchesLocation = (path: string) => {
    // Handle paths with query strings (e.g., /command/health?tab=connections)
    const [pathBase, pathQuery] = path.split('?');
    if (pathBase === '/' || pathBase === currentModuleConfig?.urlPrefix) {
      if (pathQuery) {
        return location.pathname === pathBase && location.search === `?${pathQuery}`;
      }
      return location.pathname === pathBase;
    }
    if (pathQuery) {
      return location.pathname === pathBase && location.search === `?${pathQuery}`;
    }
    return location.pathname === pathBase || location.pathname.startsWith(`${pathBase}/`);
  };

  // Every destination the current module's nav can reach, groups included.
  const navPaths = navItems.flatMap((item) => [
    item.path,
    ...(item.children?.map((child) => child.path) ?? []),
  ]);

  /**
   * A nav path is active only when no *more specific* nav path also matches.
   * Without this, a group whose landing page is a parent route (e.g.
   * `/investors/admin`) highlights on every page beneath it and two entries
   * look selected at once.
   */
  const isActive = (path: string) => {
    if (!matchesLocation(path)) return false;
    return !navPaths.some(
      (other) =>
        other !== path &&
        (other.startsWith(`${path}/`) || other.startsWith(`${path}?`)) &&
        matchesLocation(other)
    );
  };

  const navLinkClass = (path: string) => `
    px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap
    transition-all duration-150
    ${
      isActive(path)
        ? 'bg-navy-50 text-navy-900'
        : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
    }
  `.trim();

  // Handle null currentModule (home page)
  const currentModuleConfig = currentModule ? MODULES[currentModule] : null;
  const moduleOrder: ModuleId[] = ['P1', 'P2', 'P4', 'P5', 'P7', 'P8', 'P9', 'P10', 'P11', 'P12', 'P13', 'P14', 'P15', 'P16', 'P17', 'P18', 'P19', 'P20', 'AI_SYSTEMS', 'P6', 'TRAINING', 'RUDY', 'PULSE', 'SHOP'];

  return (
    <nav className="bg-white border-b border-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-navy-600 rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-sm">OH</span>
              </div>
              <span className="hidden sm:block text-lg font-semibold text-slate-900">{title}</span>
            </Link>

            {/* Module Selector */}
            <div className="relative min-w-0" ref={moduleMenuRef}>
              <button
                onClick={() => setShowModuleMenu(!showModuleMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors max-w-full"
              >
                <ModuleIcon moduleId={currentModule} className="w-4 h-4 text-slate-600 shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate max-w-[6rem] sm:max-w-none">
                  {currentModuleConfig?.shortName ?? 'Home'}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-500 transition-transform ${showModuleMenu ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Module Dropdown */}
              {showModuleMenu && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                  {/* Home option */}
                  <Link
                    to="/"
                    onClick={() => setShowModuleMenu(false)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${
                      currentModule === null
                        ? 'bg-navy-50 text-navy-900'
                        : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <HomeIcon className="w-5 h-5 text-slate-600" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Home</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        Platform overview and modules
                      </div>
                    </div>
                    {currentModule === null && (
                      <svg className="w-4 h-4 text-navy-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </Link>
                  <div className="border-t border-slate-200 my-1"></div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Modules
                  </div>
                  {moduleOrder
                    .filter((moduleId) => hasModuleAccess(moduleId))
                    .map((moduleId) => {
                    const module = MODULES[moduleId];
                    const isCurrentModule = currentModule === moduleId;
                    const isExternal = !!module.externalUrl;
                    return (
                      <button
                        key={moduleId}
                        onClick={() => {
                          setCurrentModule(moduleId);
                          if (!isExternal) setShowModuleMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${
                          isCurrentModule
                            ? 'bg-navy-50 text-navy-900'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <ModuleIcon moduleId={moduleId} className="w-5 h-5 text-slate-600" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{module.name}</span>
                            {isExternal && (
                              <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {module.description}
                          </div>
                        </div>
                        {isCurrentModule && (
                          <svg className="w-4 h-4 text-navy-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="hidden xl:block w-px h-6 bg-slate-200"></div>

            {/* Module-specific Navigation. The whole strip needs about 1,200px; below the `xl` breakpoint
                (a half-width window is ~960px) it collapses into the Menu button so no tab, FSR Review
                included, is ever clipped behind the search and user buttons (ISS-573). */}
            <div className="hidden xl:flex items-center gap-1">
              {navItems.map((item) => (
                item.children ? (
                  // Dropdown nav item
                  <div key={item.path} className="relative" ref={(el) => setNavDropdownRef(item.label, el)}>
                    <div
                      className={`
                        flex items-center rounded-md transition-all duration-150
                        ${item.children.some(child => isActive(child.path)) || isActive(item.path)
                          ? 'bg-navy-50 text-navy-900'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                        }
                      `}
                    >
                      <Link
                        to={item.path}
                        className="px-3 py-2 text-sm font-medium whitespace-nowrap"
                        onClick={() => setOpenDropdown(null)}
                        onMouseEnter={() => prefetch(item.path)}
                      >
                        {item.label}
                        <CountBadge count={groupBadge(item)} />
                      </Link>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                        className="pr-2 py-2 -ml-1"
                        aria-label={`${item.label} submenu`}
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    {openDropdown === item.label && (
                      <div className="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setOpenDropdown(null)}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              isActive(child.path)
                                ? 'bg-navy-50 text-navy-900 font-medium'
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            {child.label}
                            {child.path === LINK_REVIEW_PATH && <LinkReviewNavBadge />}
                            <CountBadge count={child.badge ?? 0} />
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.cta ? (
                  // A call to action in the bar (Quoting's New Quote): a link styled as <Button variant="brandTint">.
                  <Link key={item.path} to={item.path} className={buttonClasses('brandTint', 'sm', 'whitespace-nowrap')} onMouseEnter={() => prefetch(item.path)}>
                    {item.label}
                  </Link>
                ) : item.icon === 'settings' ? (
                  // Icon-only nav link (gear)
                  <Link key={item.path} to={item.path} className={`${navLinkClass(item.path)} flex items-center min-h-[44px] min-w-[44px] justify-center`} aria-label={item.label}>
                    <Settings className="w-5 h-5" />
                  </Link>
                ) : (
                  // Simple nav link
                  <Link key={item.path} to={item.path} className={`${navLinkClass(item.path)} flex items-center gap-1.5`} onMouseEnter={() => prefetch(item.path)}>
                    {item.label}
                    {item.badge != null && item.badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </Link>
                )
              ))}

            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* Global Search Button — only when Typesense is enabled */}
            {isTypesenseEnabled() && (
              <button
                onClick={openSearch}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200 border border-slate-200 transition-colors"
                aria-label="Open search"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Search...</span>
                <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-white rounded border border-slate-300">
                  ⌘K
                </kbd>
              </button>
            )}

            {/* Mobile Nav Menu Button - only show when there are nav items */}
            {navItems.length > 0 && (
              <div className="relative xl:hidden" ref={mobileNavRef}>
                <button
                  onClick={() => setShowMobileNav(!showMobileNav)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                  aria-label={menuBadge > 0 ? `Toggle navigation menu (${menuBadge} pending)` : 'Toggle navigation menu'}
                >
                  <svg
                    className="w-5 h-5 text-slate-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {showMobileNav ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                  <span className="hidden sm:inline text-xs font-medium text-slate-600">Menu</span>
                  <CountBadge count={menuBadge} bare />
                </button>

                {/* Mobile Navigation Dropdown */}
                {showMobileNav && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                    {navItems.map((item) => (
                      item.children ? (
                        // Dropdown items shown as a group in mobile
                        <React.Fragment key={item.path}>
                          <Link
                            to={item.path}
                            onClick={() => setShowMobileNav(false)}
                            className={`block px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                              // A group whose path is also one of its rows lights that row, not the heading too.
                              location.pathname === item.path && !item.children.some((c) => c.path === item.path)
                                ? 'text-navy-900 bg-navy-50'
                                : 'text-slate-500 hover:bg-slate-100'
                            }`}
                          >
                            {item.label}
                          </Link>
                          {/* No count on this heading: the menu shows every row under it, so the row carries it once. */}
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setShowMobileNav(false)}
                              className={`block px-4 py-2 pl-6 text-sm transition-colors ${
                                isActive(child.path)
                                  ? 'bg-navy-50 text-navy-900 font-medium'
                                  : 'text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              {child.label}
                              {child.path === LINK_REVIEW_PATH && <LinkReviewNavBadge />}
                              <CountBadge count={child.badge ?? 0} />
                            </Link>
                          ))}
                        </React.Fragment>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setShowMobileNav(false)}
                          className={`flex items-center justify-between px-4 py-2 text-sm transition-colors min-h-[44px] ${
                            isActive(item.path)
                              ? 'bg-navy-50 text-navy-900 font-medium'
                              : 'text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {item.icon === 'settings' && <Settings className="w-4 h-4" />}
                            {item.label}
                          </span>
                          {item.badge != null && item.badge > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
              >
                <UserAvatar user={user} size="sm" />
                {/* Capped from xl (where the module links show): the bar is 1,216 px wide at any screen size and the module links fill all but ~118 px of the
                    name's share, so a long name ("Maximilian Fernandez") ran over the last link (measured 2026-10-31). */}
                <span className="hidden sm:block text-sm font-medium text-slate-700 sm:max-w-none xl:max-w-[6.5rem] truncate" title={user.displayName}>
                  {user.displayName}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-600 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                  <Link
                    to="/profile"
                    onClick={() => setShowUserMenu(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    Profile Settings
                  </Link>
                  {myVehicle?.hasVehicle && (
                    <Link
                      to="/my-vehicle"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      My Vehicle
                    </Link>
                  )}
                  <Link
                    to="/service-analytics"
                    onClick={() => setShowUserMenu(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    Service Analytics
                  </Link>

                  {isOwner && (
                    <>
                      <div className="border-t border-slate-200 my-1"></div>
                      <Link
                        to="/admin/users"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>People Management</span>
                          <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded">Admin</span>
                        </div>
                      </Link>
                      <Link
                        to="/organization"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>Organization Settings</span>
                          <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded">Admin</span>
                        </div>
                      </Link>
                      <Link
                        to="/admin/role-tags"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>Role Tags</span>
                          <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded">Admin</span>
                        </div>
                      </Link>
                    </>
                  )}

                  {isAdmin() && (
                    <Link
                      to="/admin/email-health"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span>Email Health</span>
                        <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded">Admin</span>
                      </div>
                    </Link>
                  )}

                  {canAccessErrorTracking && (
                    <>
                      <div className="border-t border-slate-200 my-1"></div>
                      <Link
                        to="/error-tracking"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>Product Analytics</span>
                          <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded">Dev</span>
                        </div>
                      </Link>
                    </>
                  )}

                  {canAccessIssueTriage(user) && (
                    <>
                    {!canAccessErrorTracking && <div className="border-t border-slate-200 my-1"></div>}
                    <Link
                      to="/admin/issue-triage"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span>Issue Triage</span>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Dev</span>
                      </div>
                    </Link>
                    </>
                  )}

                  <div className="border-t border-slate-200 my-1"></div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </nav>
  );
}
